/**
 * Поле ответа на вопрос анкеты.
 *
 * Вид поля выбирается по типу вопроса из конфига: в коде вопросов нет
 * (правило №2 §17). Числовые поля вызывают цифровую клавиатуру,
 * суммы вводятся с разделением разрядов (§16.6).
 */

import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { AnswerValue, Question } from '../api/types'

interface Props {
  question: Question
  value: AnswerValue
  unknownMarker: string
  onChange: (value: AnswerValue) => void
}

/**
 * Варианты ответа для вопроса с `options_from`.
 *
 * Города и банки не лежат в конфиге анкеты: они приходят из снимка
 * рабочей таблицы и меняются вместе с ней (§14.1).
 */
function useReferenceOptions(question: Question) {
  const [options, setOptions] = useState<string[] | null>(null)
  const [warning, setWarning] = useState<string | null>(null)

  useEffect(() => {
    if (question.options_from == null) {
      setOptions(null)
      return
    }
    const name = question.options_from.replace(/^reference\./, '')
    let cancelled = false

    api
      .reference(name)
      .then((result) => {
        if (cancelled) return
        setOptions(result.значения)
        setWarning(result.предупреждение ?? null)
      })
      .catch((error: unknown) => {
        if (!cancelled) setWarning(error instanceof Error ? error.message : String(error))
      })

    return () => {
      cancelled = true
    }
  }, [question.options_from])

  return { options, warning }
}

function formatThousands(raw: string): string {
  const digits = raw.replace(/\D/g, '')
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

export function QuestionField({ question, value, unknownMarker, onChange }: Props) {
  const isUnknown = value === unknownMarker
  const { options: referenceOptions, warning } = useReferenceOptions(question)

  // Пустой список — тоже полноценный ответ (например, детей нет): без
  // этого «Дальше» остаётся выключенной, пока не добавлена первая строка.
  useEffect(() => {
    if (question.type === 'list' && value === null) onChange([])
  }, [question.id, question.type, value, onChange])

  if (isUnknown) {
    return <p className="muted">Отмечено «не знаю» — расчёт пойдёт по осторожному сценарию.</p>
  }

  // Список из справочника длинный (почти 200 городов), поэтому он
  // показывается выпадающим списком, а не плиткой вариантов.
  if (question.options_from != null) {
    if (warning != null) return <p className="error">{warning}</p>
    if (referenceOptions == null) return <p className="muted">Загружаю список…</p>
    return (
      <select
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => onChange(event.target.value || null)}
      >
        <option value="">— выберите —</option>
        {/* Справочник + свои варианты вопроса («другой банк (нет в списке)»). */}
        {[...referenceOptions, ...(question.options ?? [])].map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    )
  }

  switch (question.type) {
    case 'single_select':
      return (
        <div className="options">
          {(question.options ?? []).map((option) => (
            <label
              key={option}
              className={`option${value === option ? ' selected' : ''}`}
            >
              <input
                type="radio"
                name={question.id}
                checked={value === option}
                onChange={() => onChange(option)}
              />
              {option}
            </label>
          ))}
        </div>
      )

    case 'multi_select': {
      const selected = Array.isArray(value) ? (value as string[]) : []
      return (
        <div className="options">
          {(question.options ?? []).map((option) => (
            <label
              key={option}
              className={`option${selected.includes(option) ? ' selected' : ''}`}
            >
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={() =>
                  onChange(
                    selected.includes(option)
                      ? selected.filter((item) => item !== option)
                      : [...selected, option],
                  )
                }
              />
              {option}
            </label>
          ))}
        </div>
      )
    }

    case 'bool':
      return (
        <div className="options">
          {[
            { label: 'Да', boolValue: true },
            { label: 'Нет', boolValue: false },
          ].map(({ label, boolValue }) => (
            <label
              key={label}
              className={`option${value === boolValue ? ' selected' : ''}`}
            >
              <input
                type="radio"
                name={question.id}
                checked={value === boolValue}
                onChange={() => onChange(boolValue)}
              />
              {label}
            </label>
          ))}
        </div>
      )

    case 'money':
      return (
        <input
          // Цифровая клавиатура на телефоне; разряды разделяются пробелами.
          inputMode="numeric"
          autoComplete="off"
          value={value == null ? '' : formatThousands(String(value))}
          onChange={(event) => {
            const digits = event.target.value.replace(/\D/g, '')
            onChange(digits === '' ? null : Number(digits))
          }}
          placeholder="0 ₽"
        />
      )

    case 'number':
    case 'percent':
      return (
        <input
          inputMode="decimal"
          value={value == null ? '' : String(value)}
          onChange={(event) => {
            const raw = event.target.value.replace(',', '.')
            onChange(raw === '' ? null : Number(raw))
          }}
        />
      )

    case 'date':
      return (
        <input
          type="date"
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value || null)}
        />
      )

    case 'list': {
      if (value === null) return null

      const fields = question.item_fields ?? []

      if (fields.length > 0) {
        const rows = (Array.isArray(value) ? value : []) as Record<string, unknown>[]
        return (
          <div className="list-field">
            {rows.map((row, i) => (
              <div className="card list-row-group" key={i}>
                {fields.map((f) => {
                  const kind = question.item_types?.[f]
                  const cell = row[f]
                  const set = (fieldValue: unknown) => {
                    const next = rows.slice()
                    next[i] = { ...row, [f]: fieldValue }
                    onChange(next)
                  }
                  let input
                  if (kind?.type === 'single_select') {
                    const options = kind.options ?? []
                    // Старые анкеты: текст не из списка остаётся видимым вариантом.
                    const legacy = typeof cell === 'string' && cell !== '' && !options.includes(cell)
                    input = (
                      <select value={typeof cell === 'string' ? cell : ''} onChange={(e) => set(e.target.value)}>
                        <option value="">—</option>
                        {legacy && <option value={cell as string}>{cell as string}</option>}
                        {options.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    )
                  } else if (kind?.type === 'money') {
                    input = (
                      <input
                        inputMode="numeric"
                        autoComplete="off"
                        value={cell == null || cell === '' ? '' : formatThousands(String(cell))}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, '')
                          set(digits === '' ? '' : Number(digits))
                        }}
                        placeholder="0 ₽"
                      />
                    )
                  } else if (kind?.type === 'date') {
                    const iso = typeof cell === 'string' && /^\d{4}-\d{2}-\d{2}/.test(cell)
                    input = (
                      <>
                        <input
                          type="date"
                          value={iso ? (cell as string).slice(0, 10) : ''}
                          onChange={(e) => set(e.target.value)}
                        />
                        {!iso && typeof cell === 'string' && cell !== '' && (
                          <span className="muted"> было указано: {cell}</span>
                        )}
                      </>
                    )
                  } else {
                    input = (
                      <input
                        type="text"
                        value={typeof cell === 'string' ? cell : cell == null ? '' : String(cell)}
                        onChange={(e) => set(e.target.value)}
                      />
                    )
                  }
                  return (
                    <div key={f}>
                      <label className="field-label">{f}</label>
                      {input}
                    </div>
                  )
                })}
                <button type="button" onClick={() => onChange(rows.filter((_, idx) => idx !== i))}>
                  Убрать
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => onChange([...rows, Object.fromEntries(fields.map((f) => [f, '']))])}
            >
              + Добавить
            </button>
            {rows.length === 0 && <p className="muted">Если пунктов нет — можно сразу нажать «Дальше».</p>}
          </div>
        )
      }

      const values = (Array.isArray(value) ? value : []) as string[]
      return (
        <div className="list-field">
          {values.map((v, i) => (
            <div className="card-row" key={i}>
              <input
                type={question.item_type === 'date' ? 'date' : 'text'}
                value={v ?? ''}
                onChange={(event) => {
                  const next = values.slice()
                  next[i] = event.target.value
                  onChange(next)
                }}
              />
              <button type="button" onClick={() => onChange(values.filter((_, idx) => idx !== i))}>
                Убрать
              </button>
            </div>
          ))}
          <button type="button" onClick={() => onChange([...values, ''])}>
            + Добавить
          </button>
          {values.length === 0 && <p className="muted">Если пунктов нет — можно сразу нажать «Дальше».</p>}
        </div>
      )
    }

    default:
      return (
        <textarea
          rows={3}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value || null)}
        />
      )
  }
}
